import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $cardLimitStore, $cardStore, $profileFeatureStore } from '@lib/store';
import { useCardFlow, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { AccountInfo, Users, CardSidebar, TransactionsTable, View, PageContainer } from '@widgets';

export const CardPage = () => {
  const params = useParams<{ id: string }>();
  const { navigate } = useRouteHook();
  const id = useMemo(() => (params.id ? parseInt(params.id) : undefined), [params]);

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const cardStore = useUnit($cardStore.store);

  const { t } = useTranslation('card');

  useEffect(() => {
    if (!profileFeatureStore.data?.cardFlows?.length) navigate(RoutePaths.dashboard.onNavigate());
    if (!id) return;
    $cardStore.events.getCard({ id });
    return () => $cardStore.events.clear();
  }, [id]);

  useEffect(() => {
    if (cardStore.loading) return;
    if (cardStore.error !== 'Action is forbidden.') return;
    $cardStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [cardStore.loading, cardStore.error]);

  useEffect(() => {
    if (!cardStore.data || !profileFeatureStore.data?.cardFlows?.find((x) => x.cardId === id)) return;
    $cardLimitStore.events.getLimit({ id: id!, cardKind: cardStore.data.cardKind });
    return () => $cardLimitStore.events.clear();
  }, [cardStore.data?.cardId]);

  const cardFlow = useCardFlow(profileFeatureStore.data?.cardFlows, id);
  return (
    <PageContainer title={t('title')} back backText={t('backText')} return>
      <View
        loading={cardStore.loading}
        error={cardStore.error}
        onRefresh={() => {
          $cardStore.events.getCard({ id: id! });
          if (!cardStore.data || !profileFeatureStore.data?.cardFlows?.find((x) => x.cardId === id)) return;
          $cardLimitStore.events.getLimit({ id: id!, cardKind: cardStore.data.cardKind });
        }}
        noData={!cardStore.data}
      >
        {cardStore.data && (
          <div className={cn('flex gap-6', PaletteUtils.isVcfcPalette && 'max-md:flex-col')}>
            <div
              className={cn(
                'flex h-fit flex-auto flex-col',
                PaletteUtils.isInsigniaPalette && 'gap-6',
                PaletteUtils.isVcfcPalette && 'rounded-lg border border-gray-black bg-white dark:bg-soft-black',
              )}
            >
              <AccountInfo card={cardStore.data} />
              {profileFeatureStore.data?.userManagement?.viewUsers &&
                profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions &&
                id && <Users cardId={id!} />}
              {cardFlow?.viewTransactions && <TransactionsTable cardId={cardStore.data.cardId} />}
            </div>
            <CardSidebar className={cn('w-80', PaletteUtils.isVcfcPalette && 'max-md:w-full')} card={cardStore.data} />
          </div>
        )}
      </View>
    </PageContainer>
  );
};
